import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, Plus, Pencil, Trash2, Users, UserCheck, Calendar,
  ChevronDown, ChevronUp, Save, X, MapPin, GraduationCap, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const GestionCentres = ({ user }) => {
  const [centres, setCentres] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [formateurs, setFormateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCentre, setEditingCentre] = useState(null);
  const [expandedCentre, setExpandedCentre] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    google_calendar_code: '',
    responsable_id: '',
    formateurs_ids: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [centresRes, responsablesRes, formateursRes] = await Promise.all([
        axios.get('/centres'),
        axios.get('/responsables-pedagogiques').catch(() => ({ data: [] })),
        axios.get('/formateurs')
      ]);

      setCentres(centresRes.data || []);
      setResponsables(responsablesRes.data || []);
      setFormateurs(formateursRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const initDefaultCentres = async () => {
    try {
      const res = await axios.post('/centres/init-default');
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'initialisation');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCentre) {
        await axios.put(`/centres/${editingCentre.centre_id}`, formData);
        toast.success('Centre mis à jour');
      } else {
        await axios.post('/centres', formData);
        toast.success('Centre créé');
      }
      
      setShowForm(false);
      setEditingCentre(null);
      setFormData({
        nom: '',
        description: '',
        google_calendar_code: '',
        responsable_id: '',
        formateurs_ids: []
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (centre) => {
    setEditingCentre(centre);
    setFormData({
      nom: centre.nom || '',
      description: centre.description || '',
      google_calendar_code: centre.google_calendar_code || '',
      responsable_id: centre.responsable_id || '',
      formateurs_ids: centre.formateurs_ids || []
    });
    setShowForm(true);
  };

  const handleDelete = async (centre) => {
    if (!window.confirm(`Supprimer le centre "${centre.nom}" ?`)) return;
    
    try {
      await axios.delete(`/centres/${centre.centre_id}`);
      toast.success('Centre supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleFormateur = (formateurId) => {
    setFormData(prev => ({
      ...prev,
      formateurs_ids: prev.formateurs_ids.includes(formateurId)
        ? prev.formateurs_ids.filter(id => id !== formateurId)
        : [...prev.formateurs_ids, formateurId]
    }));
  };

  if (user.role !== 'centre') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">Accès réservé au Centre de Formation</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-centres">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A] mb-2">Gestion des Centres</h1>
          <p className="text-slate-600">Gérer les centres de formation, responsables et formateurs</p>
        </div>
        <div className="flex gap-2">
          {centres.length === 0 && (
            <Button
              onClick={initDefaultCentres}
              variant="outline"
              className="border-[#8DC63F] text-[#8DC63F] hover:bg-[#8DC63F]/10"
            >
              Initialiser Atlantique & Occitanie
            </Button>
          )}
          <Button
            onClick={() => {
              setShowForm(true);
              setEditingCentre(null);
              setFormData({
                nom: '',
                description: '',
                google_calendar_code: '',
                responsable_id: '',
                formateurs_ids: []
              });
            }}
            className="bg-[#8DC63F] hover:bg-[#7db336]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau Centre
          </Button>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#0F172A]">
              {editingCentre ? 'Modifier le Centre' : 'Nouveau Centre'}
            </h2>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingCentre(null);
              }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nom">Nom du Centre *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Atlantique"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Centre de formation de la côte atlantique"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="google_calendar_code">Google Calendrier (code d'intégration)</Label>
              <textarea
                id="google_calendar_code"
                value={formData.google_calendar_code}
                onChange={(e) => setFormData({ ...formData, google_calendar_code: e.target.value })}
                placeholder='<iframe src="https://calendar.google.com/calendar/embed?src=..." ...></iframe>'
                className="w-full min-h-[100px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F] resize-y"
              />
              <p className="text-xs text-slate-500">
                Collez le code d'intégration iframe complet depuis Google Calendar (Paramètres → Intégrer le calendrier)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsable_id">Responsable Pédagogique</Label>
              <select
                id="responsable_id"
                value={formData.responsable_id}
                onChange={(e) => setFormData({ ...formData, responsable_id: e.target.value })}
                className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              >
                <option value="">-- Aucun responsable assigné --</option>
                {responsables
                  .filter(r => !r.centre_id || r.centre_id === editingCentre?.centre_id)
                  .map(resp => (
                    <option key={resp.user_id} value={resp.user_id}>
                      {resp.user_info?.prenom} {resp.user_info?.nom} ({resp.user_info?.email})
                    </option>
                  ))}
              </select>
              {responsables.length === 0 && (
                <p className="text-xs text-amber-600">
                  Aucun responsable pédagogique inscrit. Ils doivent s'inscrire et être validés.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Formateurs assignés</Label>
              <div className="border border-slate-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                {formateurs.length === 0 ? (
                  <p className="text-slate-500 text-sm">Aucun formateur disponible</p>
                ) : (
                  <div className="space-y-2">
                    {formateurs.map(form => (
                      <label
                        key={form.user_id}
                        className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-lg cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.formateurs_ids.includes(form.user_id)}
                          onChange={() => toggleFormateur(form.user_id)}
                          className="w-4 h-4 text-[#8DC63F] rounded"
                        />
                        <span className="text-sm">
                          {form.prenom} {form.nom}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Un formateur peut être assigné à plusieurs centres
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingCentre(null);
                }}
              >
                Annuler
              </Button>
              <Button type="submit" className="bg-[#8DC63F] hover:bg-[#7db336]">
                <Save className="h-4 w-4 mr-2" />
                {editingCentre ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des centres */}
      <div className="space-y-4">
        {centres.length === 0 ? (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-12 text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Aucun centre configuré</h3>
            <p className="text-slate-600 mb-4">
              Cliquez sur "Initialiser Atlantique & Occitanie" pour créer les centres par défaut
            </p>
          </div>
        ) : (
          centres.map(centre => (
            <div
              key={centre.centre_id}
              className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
            >
              {/* En-tête du centre */}
              <div
                className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedCentre(expandedCentre === centre.centre_id ? null : centre.centre_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-lg flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-[#8DC63F]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#0F172A]">{centre.nom}</h3>
                      <p className="text-sm text-slate-600">{centre.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {centre.nb_stagiaires} stagiaires
                      </span>
                      <span className="flex items-center gap-1">
                        <GraduationCap className="h-4 w-4" />
                        {centre.nb_formateurs} formateurs
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {centre.nb_tuteurs || 0} tuteurs
                      </span>
                    </div>
                    {expandedCentre === centre.centre_id ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Détails du centre */}
              {expandedCentre === centre.centre_id && (
                <div className="border-t border-slate-200 p-6 bg-slate-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Responsable */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-blue-500" />
                        Responsable Pédagogique
                      </h4>
                      {centre.responsable_info ? (
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold">
                              {centre.responsable_info.prenom?.[0]}{centre.responsable_info.nom?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">
                              {centre.responsable_info.prenom} {centre.responsable_info.nom}
                            </p>
                            <p className="text-sm text-slate-600">{centre.responsable_info.email}</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">Aucun responsable assigné</p>
                      )}
                    </div>

                    {/* Calendrier */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-green-500" />
                        Google Calendar
                      </h4>
                      {centre.google_calendar_code ? (
                        <p className="text-sm text-slate-600 break-all">
                          {centre.google_calendar_code}
                        </p>
                      ) : (
                        <p className="text-slate-500 text-sm">Non configuré</p>
                      )}
                    </div>
                  </div>

                  {/* Formateurs et Tuteurs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    {/* Formateurs */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                        <GraduationCap className="h-4 w-4 text-purple-500" />
                        Formateurs ({centre.nb_formateurs || 0})
                      </h4>
                      {centre.formateurs_info && centre.formateurs_info.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {centre.formateurs_info.map(form => (
                            <div key={form.user_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                <span className="text-purple-600 font-semibold text-xs">
                                  {form.prenom?.[0]}{form.nom?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-sm">{form.prenom} {form.nom}</p>
                                <p className="text-xs text-slate-500">{form.email}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">Aucun formateur assigné</p>
                      )}
                    </div>

                    {/* Tuteurs */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-orange-500" />
                        Tuteurs ({centre.nb_tuteurs || 0})
                      </h4>
                      {centre.tuteurs_info && centre.tuteurs_info.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {centre.tuteurs_info.map(tut => (
                            <div key={tut.user_id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                <span className="text-orange-600 font-semibold text-xs">
                                  {tut.prenom?.[0]}{tut.nom?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-sm">{tut.prenom} {tut.nom}</p>
                                <p className="text-xs text-slate-500">{tut.email}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm">Aucun tuteur lié à ce centre</p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(centre);
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(centre);
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default GestionCentres;
